import React from 'react';
import {
    Activity, AlertCircle, CheckCircle2, Clock, Zap
} from 'lucide-react';
import {
    cn
} from 'shared/lib/utils';

/**
 * NdjsonEventRenderer — renders the streaming event tail for both modes:
 *   - mode="opcode"   : `events` is CPIEResponse[] from useOpcodeStream
 *   - mode="assistant": `events` is the raw A2A SSE event list from a2aClient
 *
 * Visual goal: tight, scannable trace. Step rows are color-coded by status,
 * delta rows are muted, errors are red, taskId pinned at the top.
 *
 * For the assistant chat-style display, the dock builds bubbles itself; this
 * renderer is for the trace/expand view inside an assistant bubble OR for
 * the live opcode-mode panel.
 */
export default function NdjsonEventRenderer({
    events, mode = 'opcode', taskId, error, isRunning
}) {
    if (!events || events.length === 0) {
        if (error) return <ErrorRow text={error} />;
        if (isRunning) return <RunningHint />;
        return null;
    }

    const rows = mode === 'opcode'
        ? normalizeCpieEvents(events)
        : normalizeA2aEvents(events);

    return (
        <div className="space-y-1.5 text-[11.5px] leading-tight">
            {taskId && (
                <div className="flex items-center gap-1.5 text-[10.5px] text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span className="font-mono">task {taskId}</span>
                </div>
            )}
            {rows.map((r, i) => (
                <EventRow key={i} row={r} />
            ))}
            {error && <ErrorRow text={error} />}
            {isRunning && <RunningHint />}
        </div>
    );
}

function EventRow({
    row
}) {
    const Icon = row.icon || Activity;
    return (
        <div
            className={cn(
                'flex items-start gap-2 rounded-md border px-2 py-1.5',
                row.kind === 'error' && 'border-destructive/40 bg-destructive/5',
                row.kind === 'success' && 'border-emerald-300/50 bg-emerald-50/40',
                row.kind === 'step' && 'border-border bg-card',
                row.kind === 'meta' && 'border-dashed bg-muted/30',
                row.kind === 'delta' && 'border-transparent bg-transparent py-0.5'
            )}
        >
            <Icon
                className={cn(
                    'mt-0.5 h-3 w-3 flex-shrink-0',
                    row.kind === 'error' && 'text-destructive',
                    row.kind === 'success' && 'text-emerald-700',
                    row.kind === 'step' && 'text-primary',
                    row.kind === 'meta' && 'text-muted-foreground',
                    row.kind === 'delta' && 'text-muted-foreground'
                )}
            />
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <span className="font-medium">{row.label}</span>
                    {row.subLabel && <span className="text-[10.5px] text-muted-foreground">{row.subLabel}</span>}
                </div>
                {row.detail && (
                    <pre className="mt-0.5 whitespace-pre-wrap break-words font-mono text-[10.5px] text-muted-foreground">
                        {typeof row.detail === 'string' ? row.detail : safeStringify(row.detail)}
                    </pre>
                )}
            </div>
        </div>
    );
}

function humanizeErrorText(text) {
    if (!text || typeof text !== 'string') return text;
    try {
        const obj = JSON.parse(text);
        const inner = obj?.error || obj;
        if (inner?.message) return inner.message;
    } catch {
        // not JSON — return as-is
    }
    return text;
}

function ErrorRow({
    text
}) {
    const display = humanizeErrorText(text);
    return (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1.5">
            <AlertCircle className="mt-0.5 h-3 w-3 flex-shrink-0 text-destructive" />
            <div className="min-w-0 flex-1 text-[11.5px] text-destructive">
                {display}
            </div>
        </div>
    );
}

function RunningHint() {
    return (
        <div className="flex items-center gap-2 px-2 py-1 text-[11.5px] italic text-muted-foreground">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-primary" />
            running…
        </div>
    );
}

// ── Normalizers ─────────────────────────────────────────────────────────────

// Translate the A-0008.05 NDJSON stream into uniform rows.
//
// Mind emits one event per line through the bex-server WS proxy:
//   { type: "begin",  task_id }
//   { type: "step",   id, status: "start" | "end", output? }
//   { type: "delta",  step, text }
//   { type: "result", ok, output? | error? }
//   { type: "error",  step_id?, code, type, message }
//
// useOpcodeStream collects each parsed event into `messages[]`; we walk the
// list and produce one row per visible event. begin/step-end pairs are
// folded into a single in-progress → completed row whenever possible so the
// trace stays compact.
function normalizeCpieEvents(events) {
    const rows = [];
    // Track in-progress step rows so we can flip them to "completed" when
    // the matching end event arrives. Map<step_id, rowIndex>.
    const openSteps = new Map();

    for (const ev of events) {
        if (!ev || typeof ev !== 'object') continue;
        const t = ev.type;

        if (t === 'begin') {
            // Already shown as the taskId chip at the top — skip to keep tight.
            continue;
        }

        if (t === 'step') {
            const id = ev.id || ev.step_id || 'step';
            if (ev.status === 'start') {
                const idx = rows.length;
                rows.push({
                    kind: 'step',
                    icon: Activity,
                    label: id,
                    subLabel: ev.label || ev.kind || 'running',
                });
                openSteps.set(id, idx);
            } else if (ev.status === 'end') {
                const idx = openSteps.get(id);
                const row = {
                    kind: 'step',
                    icon: CheckCircle2,
                    label: id,
                    subLabel: ev.label || ev.kind || 'done',
                    detail: ev.output
                };
                if (idx !== undefined) {
                    rows[idx] = row;
                    openSteps.delete(id);
                } else {
                    rows.push(row);
                }
            }
            continue;
        }

        if (t === 'delta') {
            const text = ev.text || ev.token || ev.delta || safeStringify(ev);
            rows.push({
                kind: 'delta',
                icon: Zap,
                label: ev.step ? `${ev.step}` : '·',
                subLabel: 'token',
                detail: text
            });
            continue;
        }

        if (t === 'result') {
            if (ev.ok) {
                rows.push({
                    kind: 'success',
                    icon: CheckCircle2,
                    label: 'completed',
                    detail: ev.output
                });
            } else {
                const e = ev.error || {};
                // Flip any still-running step row that this error fingers as
                // failed, so the trace doesn't read "running … failed".
                if (e.step_id && openSteps.has(e.step_id)) {
                    const idx = openSteps.get(e.step_id);
                    rows[idx] = {
                        kind: 'error',
                        icon: AlertCircle,
                        label: e.step_id,
                        subLabel: 'failed'
                    };
                    openSteps.delete(e.step_id);
                }
                rows.push({
                    kind: 'error',
                    icon: AlertCircle,
                    label: e.code || e.type || 'failed',
                    subLabel: e.step_id || '',
                    detail: e.message || safeStringify(ev.error || ev)
                });
            }
            continue;
        }

        if (t === 'error') {
            rows.push({
                kind: 'error',
                icon: AlertCircle,
                label: ev.code || ev.type || 'error',
                subLabel: ev.step_id || '',
                detail: ev.message || safeStringify(ev)
            });
            continue;
        }

        // Unknown event — surface as raw meta so nothing is silently dropped.
        rows.push({
            kind: 'meta',
            icon: Activity,
            label: t || 'event',
            detail: ev
        });
    }
    return rows;
}

// Translate A2A SSE events into uniform rows. A2A events are JSON-RPC
// envelopes with `result` carrying Task / TaskStatusUpdateEvent /
// TaskArtifactUpdateEvent / Message. We read the most useful fields for
// each shape so the trace is informative even though shapes vary.
function normalizeA2aEvents(events) {
    const rows = [];
    for (const env of events) {
        if (env?._error) {
            rows.push({
                kind: 'error',
                icon: AlertCircle,
                label: 'stream error',
                detail: env._error
            });
            continue;
        }
        const r = env?.result;
        if (!r || typeof r !== 'object') continue;

        // TaskStatusUpdateEvent
        if (r.kind === 'status-update' || r.status?.state) {
            const state = r.status?.state || r.state;
            rows.push({
                kind: state && state.includes('FAILED') ? 'error' : 'meta',
                icon: Activity,
                label: humanizeTaskState(state) || 'status'
            });
        }
        // TaskArtifactUpdateEvent — running output bits
        if (r.kind === 'artifact-update' || r.artifact) {
            const a = r.artifact;
            const partTexts = (a?.parts || [])
                .map((p) => (p.type === 'text' ? p.text : null))
                .filter(Boolean);
            if (partTexts.length) {
                rows.push({
                    kind: 'delta',
                    icon: Zap,
                    label: a.name || 'artifact',
                    detail: partTexts.join('')
                });
            }
        }
        // Final Task — render routing decision if present
        if (r.id && r.artifacts) {
            const exec = r.artifacts.find((x) => x.name === 'execution_result') || r.artifacts[0];
            const dataPart = exec?.parts?.find((p) => p.type === 'data');
            const routing = dataPart?.data?._bex_routing;
            if (routing) {
                rows.push({
                    kind: 'meta',
                    icon: Activity,
                    label: 'routing',
                    subLabel: routing.resolution_mode || '',
                    detail: routing.selected_opcode_id
                        ? `→ ${routing.selected_opcode_id}${routing.reason ? ` · ${routing.reason}` : ''}`
                        : routing.reason || ''
                });
            }
        }
    }
    return rows;
}

function humanizeTaskState(s) {
    if (!s) return null;
    return String(s).replace(/^TASK_STATE_/, '').toLowerCase();
}

function safeStringify(v) {
    try {
        return typeof v === 'string' ? v : JSON.stringify(v, null, 2);
    } catch {
        return String(v);
    }
}
